import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SocialIconLinks from "./SocialIconLinks";
import MobileAppDownloadCard from "./MobileAppDownloadCard";
import { SkeletonBlock, SkeletonRegion } from "./skeletons/ShopperSkeletons";
import { FOOTER_LINK_GROUPS } from "../content/siteContent";
import { EMPTY_SITE_CONTENT, fetchSiteContent } from "../services/siteService";

function FooterLink({ link }) {
  const sharedClass =
    "group inline-flex items-center gap-2 text-[15px] text-neutral-600 transition hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100";

  if (String(link.to).includes("#")) {
    return (
      <a href={link.to} className={sharedClass}>
        <span>{link.label}</span>
        <ChevronRight
          size={14}
          className="opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
        />
      </a>
    );
  }

  return (
    <Link to={link.to} className={sharedClass}>
      <span>{link.label}</span>
      <ChevronRight
        size={14}
        className="opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
      />
    </Link>
  );
}

export default function ShopFooter() {
  const [siteContent, setSiteContent] = useState(EMPTY_SITE_CONTENT);
  const [siteContentLoading, setSiteContentLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const controller = new AbortController();

    fetchSiteContent({ signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setSiteContent(data || EMPTY_SITE_CONTENT);
      })
      .catch(() => {
        if (!controller.signal.aborted) setSiteContent(EMPTY_SITE_CONTENT);
      })
      .finally(() => {
        if (!controller.signal.aborted) setSiteContentLoading(false);
      });

    return () => controller.abort();
  }, []);

  const brand = siteContent?.brand || EMPTY_SITE_CONTENT.brand;

  return (
    <footer className={`${["/about", "/sell-on-apparify", "/terms-and-conditions", "/privacy-policy"].includes(location.pathname) ? "mt-0 border-transparent" : "mt-10 border-neutral-200 dark:border-neutral-800"} border-t bg-neutral-50 pb-[calc(5rem+env(safe-area-inset-bottom))] text-neutral-900 dark:bg-[#0a0a0a] dark:text-neutral-50 md:pb-6`}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 border-b border-neutral-200 py-12 dark:border-neutral-800 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)] lg:gap-14 lg:py-16">
          <section>
            <h2 className="text-[clamp(3.75rem,9vw,8rem)] font-black leading-[0.82] tracking-[-0.075em]">
              APPARIFY.
            </h2>
            <p className="mt-7 max-w-md text-sm leading-6 text-neutral-500 dark:text-neutral-400">
              A modern apparel marketplace connecting shoppers with participating stores in
              Cavite.
            </p>
            <div className="mt-5">
              {siteContentLoading ? (
                <SkeletonRegion label="Loading social links" className="flex gap-3">
                  {Array.from({ length: 4 }, (_, index) => (
                    <SkeletonBlock key={`footer-social-skeleton-${index}`} className="h-10 w-10 rounded-full" />
                  ))}
                </SkeletonRegion>
              ) : (
                <SocialIconLinks
                  socialLinks={siteContent?.socialLinks}
                  brandName={brand.name}
                  variant="minimal"
                />
              )}
            </div>
          </section>

          <div className="min-w-0">
            <MobileAppDownloadCard brandName={brand.name} />
          </div>
        </div>

        <div className="border-b border-neutral-200 py-12 dark:border-neutral-800 lg:py-14">
          <div className="grid w-full grid-cols-1 gap-x-16 gap-y-10 sm:grid-cols-3 lg:gap-x-24">
            {FOOTER_LINK_GROUPS.map((group, index) => (
              <section
                key={group.title}
                className={`w-full max-w-56 ${index === 0
                  ? "sm:justify-self-start"
                  : index === FOOTER_LINK_GROUPS.length - 1
                    ? "sm:justify-self-end"
                    : "sm:justify-self-center"
                }`}
              >
                <h2 className="text-xs font-bold uppercase tracking-[0.28em] text-neutral-400 dark:text-neutral-500">
                  {group.title}
                </h2>
                <div className="mt-6 flex flex-col items-start gap-3.5">
                  {group.links.map((link) => (
                    <FooterLink key={link.to} link={link} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        <div className="py-5 text-xs text-neutral-500 dark:text-neutral-500">
          <p>&copy; 2026 Apparify. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
